/**
 * Content for the Projektpartner page (rendered at `/developers`), ported from
 * the approved website draft's "3f" screen.
 *
 * Kept as data because it carries public claims (the 1.200 m² / 38 Zimmer case
 * figures, the guaranteed-rent promise) that should be reviewable in one place.
 * German is the draft's source language; English is the site's EN toggle.
 */
import type { Bilingual } from "@/components/operators/landlord-data";

export type { Bilingual };

export const PARTNER_STATS: { value: string; label: Bilingual }[] = [
  {
    value: "1.200 m²",
    label: {
      de: "Bürofläche im Bestand",
      en: "existing office space",
    },
  },
  {
    value: "38",
    label: { de: "Zimmer nach dem Umbau", en: "rooms after the conversion" },
  },
];

export const DEV_WHY: { name: Bilingual; body: Bilingual }[] = [
  {
    name: { de: "Garantierte Miete", en: "Guaranteed rent" },
    body: {
      de: "Niedrige Leerstandsrate mit zuverlässigen monatlichen Einnahmen.",
      en: "A low vacancy rate with reliable monthly income.",
    },
  },
  {
    name: { de: "Full Service", en: "Full service" },
    body: {
      de: "Wir kümmern uns um Renovierung, Möblierung und Betrieb.",
      en: "We handle renovation, furnishing and operation.",
    },
  },
  {
    name: { de: "ESG-konform", en: "ESG-compliant" },
    body: {
      de: "Gemeinschaftsorientierter, nachhaltiger Betrieb.",
      en: "Community-focused, sustainable operation.",
    },
  },
  {
    name: { de: "Transparentes Reporting", en: "Transparent reporting" },
    body: {
      de: "Quartalsberichte und fester Ansprechpartner.",
      en: "Quarterly reports and a named point of contact.",
    },
  },
];

export const PARTNER_STEPS: { no: string; name: Bilingual; body: Bilingual }[] =
  [
    {
      no: "01",
      name: { de: "Erstgespräch", en: "First conversation" },
      body: {
        de: "Was coliviq macht und mit wem wir arbeiten.",
        en: "What coliviq does and who we work with.",
      },
    },
    {
      no: "02",
      name: { de: "Prüfung", en: "Assessment" },
      body: {
        de: "Wir bewerten das Potenzial Ihrer Immobilie.",
        en: "We assess your property's potential.",
      },
    },
    {
      no: "03",
      name: { de: "Vereinbarung", en: "Agreement" },
      body: {
        de: "Mietbedingungen und Renovierungsplan definieren.",
        en: "Defining the lease terms and the renovation plan.",
      },
    },
    {
      no: "04",
      name: { de: "Übergabe", en: "Handover" },
      body: {
        de: "Projektstart mit digitaler Dokumentation.",
        en: "Project start with digital documentation.",
      },
    },
    {
      no: "05",
      name: { de: "Ausbau & Betrieb", en: "Fit-out & operation" },
      body: {
        de: "Wir renovieren, möblieren und verwalten den Betrieb.",
        en: "We renovate, furnish and run the operation.",
      },
    },
    {
      no: "06",
      name: { de: "Reporting", en: "Reporting" },
      body: {
        de: "Quartalsberichte, fester Ansprechpartner, stetige Miete.",
        en: "Quarterly reports, a named contact, steady rent.",
      },
    },
    {
      no: "07",
      name: { de: "Verlängerung", en: "Renewal" },
      body: {
        de: "Langfristige Partnerschaft und Portfolio-Erweiterung.",
        en: "A long-term partnership and portfolio growth.",
      },
    },
  ];

/** Maps onto the fields `send-property-submission-email` already accepts. */
export const PARTNER_FORM: {
  field: string;
  label: Bilingual;
  hint: Bilingual;
  type?: string;
  /** Renders a select instead of a text input; values match the old form. */
  options?: { value: string; label: Bilingual }[];
}[] = [
  {
    field: "name",
    label: { de: "Vollständiger Name", en: "Full name" },
    hint: { de: "Vollständiger Name", en: "Full name" },
  },
  {
    field: "email",
    label: { de: "E-Mail-Adresse", en: "Email address" },
    hint: { de: "name@beispiel.de", en: "name@example.com" },
    type: "email",
  },
  {
    field: "phone",
    label: { de: "Telefonnummer", en: "Phone number" },
    hint: { de: "+49 151 23456789", en: "+49 151 23456789" },
    type: "tel",
  },
  {
    field: "address",
    label: { de: "Immobilienadresse", en: "Property address" },
    hint: { de: "Straße Hausnr., PLZ Stadt", en: "Street no., postcode city" },
  },
  {
    field: "propertyType",
    label: { de: "Immobilientyp", en: "Property type" },
    hint: { de: "Immobilientyp wählen", en: "Choose a property type" },
    options: [
      { value: "house", label: { de: "Haus", en: "House" } },
      { value: "apartment", label: { de: "Wohnung", en: "Apartment" } },
      {
        value: "multiunit",
        label: { de: "Mehrfamilienhaus", en: "Multi-unit building" },
      },
    ],
  },
  {
    field: "area",
    label: { de: "Gesamtfläche (m²)", en: "Total area (m²)" },
    hint: { de: "z. B. 120", en: "e.g. 120" },
    type: "number",
  },
];

export const DEV_FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: {
      de: "Wie läuft die Zusammenarbeit mit coliviq ab?",
      en: "How does a partnership with coliviq work?",
    },
    a: {
      de: "In sieben Schritten: Erstgespräch, Prüfung der Immobilie, Vereinbarung, Übergabe, Umbau und Möblierung, laufender Betrieb mit Reporting, Verlängerung.",
      en: "In seven steps: a first conversation, assessing the property, the agreement, handover, conversion and furnishing, ongoing operation with reporting, renewal.",
    },
  },
  {
    q: {
      de: "Wie wird meine Miete berechnet?",
      en: "How is my rent calculated?",
    },
    a: {
      de: "Auf Basis des erzielbaren Zimmerpreises im jeweiligen Markt, abgeleitet aus echten Vermietungsdaten aus unserem eigenen Portfolio.",
      en: "From the achievable room price in that market, derived from real letting data in our own portfolio.",
    },
  },
  {
    q: {
      de: "Bleibt die Immobilie in meinem Eigentum?",
      en: "Does the property stay mine?",
    },
    a: {
      de: "Ja. Wir mieten oder betreiben, wir kaufen nicht. Das Eigentum bleibt vollständig bei Ihnen.",
      en: "Yes. We lease or operate, we do not buy. Ownership stays entirely with you.",
    },
  },
  {
    q: {
      de: "Was passiert bei Schäden oder Reparaturen?",
      en: "What happens with damage or repairs?",
    },
    a: {
      de: "Laufende Instandhaltung und Kleinreparaturen übernehmen wir, dokumentiert im Dashboard.",
      en: "We take on routine maintenance and minor repairs, documented in the dashboard.",
    },
  },
  {
    q: {
      de: "Wie lange dauert es, bis meine Immobilie vermietet ist?",
      en: "How long until my property is let?",
    },
    a: {
      de: "Die Vermarktung startet vor der Fertigstellung. In der Regel ist das Objekt ab Übergabe belegt.",
      en: "Marketing starts before completion. As a rule the property is occupied from handover.",
    },
  },
];

/* ------------------------------------------------------------------ */
/* Über uns (3g)                                                       */
/* ------------------------------------------------------------------ */

export const ABOUT_STORY: Bilingual[] = [
  {
    de: "Wir haben coliviq gegründet, weil wir selbst WGs betreiben — und kein Tool fanden, das dazu passt.",
    en: "We founded coliviq because we run shared flats ourselves — and could not find a tool that fitted.",
  },
  {
    de: "Was es gab, war entweder für klassische Hausverwaltung gebaut oder eine reine Buchungsplattform. Keines hat abgebildet, wie Coliving wirklich funktioniert: mehrere Mietverträge in einer Wohnung, ständige Wechsel und eine Community, die gepflegt werden will.",
    en: "What existed was either built for traditional property management or was purely a booking platform. Neither reflected how coliving actually works: several leases in one flat, constant turnover, and a community that needs looking after.",
  },
  {
    de: "Also haben wir es selbst gebaut. Jede Funktion läuft zuerst in unseren eigenen WGs, bevor sie bei Mietern und Vermietern ankommt.",
    en: "So we built it ourselves. Every feature runs in our own shared flats first, before it reaches tenants and landlords.",
  },
];

export const ABOUT_VALUES: { name: Bilingual; body: Bilingual }[] = [
  {
    name: {
      de: "Erst selbst, dann für andere",
      en: "Ourselves first, then others",
    },
    body: {
      de: "Was wir anbieten, nutzen wir in unseren eigenen WGs jeden Tag.",
      en: "What we offer, we use in our own shared flats every day.",
    },
  },
  {
    name: { de: "Digital, aber persönlich", en: "Digital, but personal" },
    body: {
      de: "Alles läuft über die App — und hinter jeder Nachricht sitzt jemand aus unserem Team.",
      en: "Everything runs through the app — and behind every message is someone from our team.",
    },
  },
  {
    name: { de: "Nicht bei null anfangen", en: "Not starting from zero" },
    body: {
      de: "Wer in eine neue Stadt zieht, soll ankommen — nicht erst Möbel und Mitbewohner suchen.",
      en: "Anyone moving to a new city should arrive — not start by hunting for furniture and flatmates.",
    },
  },
];

export const FOUNDERS = {
  names: "Arber & Neel",
  role: { de: "Gründer von coliviq", en: "Founders of coliviq" },
  image: "/assets/kontakt-founders.webp",
};
